import React, { useEffect, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { Close as CloseIcon } from '@mui/icons-material';
import * as mapboxStyle from 'mui-mobile-components/kart/mapcomponent/MapboxStyle';
import AddIcon from '@mui/icons-material/Add';
import mixpanel from 'mixpanel-browser';
import { KART_SELECT_FIELD, KART_SELECT_LAST_USED_TASK, KART_SELECT_TASK, CLICK_STD_SETTINGS } from 'constants/MixPanel';
import { createSvgIcon } from '@mui/material/utils';
import { isMobileOnly } from 'react-device-detect';
import { SvgIcon, IconButton, Typography, Box } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';

const userAgent = navigator.userAgent || navigator.vendor || window.opera;
const isIOS = (isMobileOnly && /iPhone|iPad|iPod/i.test(userAgent)) || 
              (navigator.maxTouchPoints > 1 && /MacIntel/.test(navigator.userAgent));

/* Replace with your own Mapbox access token */
const MAPBOX_ACCESS_TOKEN = 'pk.eyJ1Ijoia291c2hpa3NhaSIsImEiOiJjazBua3FodDAwMG93M2NraTAzNXc3NTd2In0.lhWyF7XvC_bAEW0ZY9KcXQ';

const CustomLocationOffIcon = (props) => (
  <SvgIcon {...props}>
    {/* Outer pin */}
    <path 
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" 
      fill="#75A8AA" // Fill color for the pin
      stroke="#04696D" // Border color for the pin
      strokeWidth="1.5" 
    />
    {/* Inner circle */}
    <circle 
      cx="12" 
      cy="9" 
      r="1.5" 
      fill="#04696D" // Fill color for the circle
      stroke="#04696D" // Border color for the circle
      strokeWidth="1.5" 
    />
  </SvgIcon>
);

const CustomLocationOnIcon = (props) => (
  <SvgIcon {...props}>
    {/* Outer pin */}
    <path 
      d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" 
      fill="#04696D" // Fill color for the pin
      stroke="#04696D" // Border color for the pin
      strokeWidth="1.5" 
    />
    {/* Inner circle */}
    <circle 
      cx="12" 
      cy="9" 
      r="1.5" 
      fill="#C3DEE0" // Fill color for the circle
      stroke="#C3DEE0" // Border color for the circle
      strokeWidth="1.5" 
    />
  </SvgIcon>
);

const ArrowIcon = createSvgIcon(
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <g id="icon">
      <path id="icon_2" d="M12 4L10.59 5.41L16.17 11H4V13H16.17L10.59 18.59L12 20L20 12L12 4Z" fill="#3F4949"/>
    </g>
  </svg>,
  'ArrowIcon',
);
const initialHeight = 120;
const halfHeight = window.innerHeight * 0.5;
const maxHeight = window.innerHeight * 0.75;

const MapBox = forwardRef((props, ref) => {
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const selectedPolygonIdsRef = useRef([]);
  const [height, setHeight] = useState(halfHeight);
  const [startY, setStartY] = useState(0);
  const [currentHeight, setCurrentHeight] = useState(initialHeight);
  const [showModal, setShowModal] = useState(false);
  const [clickedArea, setClickedArea] = useState([]);
  const [selectedPolygonId, setSelectedPolygonId] = useState(null);
  const [selectedPolygonIds, setSelectedPolygonIds] = useState([]);
  const [clickedPolygonName, setClickedPolygonName] = useState([]);
  const [isRunning, setIsRunning] = useState(false);
  const [startTime, setStartTime] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0); // in ms
  const [totalHours, setTotalHours] = useState(0);
  const [remainingMinutes, setRemainingMinutes] = useState(0);
  const { openShowModal, closeSaveNewTask } = props;

  useImperativeHandle(ref, () => ({
    stopTimer: () => {
      setIsRunning(false);
    },
    // Optional: you can expose other methods like resetTimer, getElapsedTimeParts, etc.
  }));

  // Update timer every minute while running
  useEffect(() => {
    let interval;
    if (isRunning) {
      interval = setInterval(() => {
        setElapsedTime(Date.now() - startTime);
      }, 1000); // updates every second for precision
    }
    return () => clearInterval(interval);
  }, [isRunning, startTime]);

  const handleStartStop = () => {
    if (isRunning) {
      // Stopping
      setIsRunning(false);
      const finalTime = Date.now() - startTime;
      const totalMinutes = Math.floor(finalTime / 60000);
      const totalHours = Math.floor(totalMinutes / 60);
      const remainingMinutes = totalMinutes % 60;
      setTotalHours(totalHours);
      setRemainingMinutes(remainingMinutes);
    } else {
      // Starting
      setStartTime(Date.now() - elapsedTime);
      setIsRunning(true);
    }
  };

  const getElapsedTimeParts = () => {
    const totalMinutes = Math.floor(elapsedTime / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;
    return { hours, minutes };
  };

  const formatElapsedFull = () => {
    const totalSeconds = Math.floor(elapsedTime / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    if (props.deSelection && props.selectedFields.length > 0) {
      setClickedArea(props.selectedFields[0].fieldName);
      setClickedPolygonName(props.selectedFields[0].fieldName);
      setSelectedPolygonIds(props.selectedFields[0].fieldId);
    }
  }, [props.deSelection, props.selectedFields]);
  
  useEffect(() => {
    const { fieldDetails } = props;
    if (props.deSelection && props.selectedFields.length > 0) {
      setSelectedPolygonIds(() => {
        const newSelected = props.selectedFields[0].fieldId; // Always an array
        selectedPolygonIdsRef.current = newSelected; // Move to useEffect
        let fieldCoordinates = [];
  
        if (fieldDetails?.data1?.fields) {
          fieldDetails.data1.fields.forEach((data) => {
            let coordinates =
              data.gpsPoints &&
              data.gpsPoints.map((gpsPoint) => [gpsPoint.longitude, gpsPoint.lattitude]);
  
            if (coordinates.length > 0) {
              fieldCoordinates.push({
                type: "Feature",
                geometry: { type: "Polygon", coordinates: [coordinates] },
                properties: { name: data.kartDisplayName, id: data.fieldId },
              });
            }
          });
        }
  
        // Apply immediate color change to Mapbox
        fieldCoordinates.forEach((polygon) => {
          const id = polygon.properties.id;
          if (newSelected.includes(id)) { // Ensure it is an array
            mapRef.current.setPaintProperty(`field-fill-${id}`, 'fill-color', '#6df6fd');
            mapRef.current.setPaintProperty(`field-fill-${id}`, 'fill-opacity', 0.8);
          } else {
            mapRef.current.setPaintProperty(`field-fill-${id}`, 'fill-color', '#00686b');
            mapRef.current.setPaintProperty(`field-fill-${id}`, 'fill-opacity', 0.08);
          }
        });
  
        return newSelected;
      });
    }
  }, [props]);
  
  // Keep ref updated when selectedPolygonIds changes
  useEffect(() => {
    selectedPolygonIdsRef.current = selectedPolygonIds;
  }, [selectedPolygonIds]);

  useEffect(() => {
    const { activeFarm, fieldDetails, coordinatesState, userInfo, geoLocDetails } = props;

    let centerCoordinates = activeFarm.farm.location !== null
        ? [activeFarm.farm.location.longitude, activeFarm.farm.location.lattitude]
        : coordinatesState;

    if (geoLocDetails && geoLocDetails.length > 0) {
        if (!isNaN(geoLocDetails[0].longitude) && !isNaN(geoLocDetails[0].latitude)) {
            centerCoordinates = [geoLocDetails[0].longitude, geoLocDetails[0].latitude];
        }
    }

    if (!window.mapboxgl) return;
    window.mapboxgl.accessToken = MAPBOX_ACCESS_TOKEN;

    const requestLocation = () => {
      navigator.geolocation.getCurrentPosition(
          (position) => {
              const { latitude, longitude } = position.coords;
              console.log("User location detected:", latitude, longitude);
  
              centerCoordinates = [longitude, latitude];
  
              if (!mapRef.current) {
                  initMap(centerCoordinates);
              } else {
                  mapRef.current.setCenter(centerCoordinates);
              }
          },
          (error) => {
              console.log("Location permission denied or error:", error.message);
              if (!mapRef.current) {
                  initMap(centerCoordinates);
              }
          },
          { enableHighAccuracy: true, timeout: 5000 }
      );
    };

    const checkPermissionAndRequestLocation = () => {
      const locationPermission = localStorage.getItem("locationPermission");
  
      if (locationPermission === "granted") {
          return;  // Don't call requestLocation() again
      }
  
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
          if (result.state === "granted") {
              localStorage.setItem("locationPermission", "granted");
          } else if (result.state === "prompt") {
              requestLocation(); // This will ask for permission and set the localStorage
          } else {
              if (!mapRef.current) {
                  initMap(centerCoordinates);
              }
          }
      });
  };
  

    function initMap(centerCoordinates) {
        mapRef.current = new window.mapboxgl.Map({
            container: mapContainerRef.current,
            style: 'mapbox://styles/mimirovarmesum/cm4u0tu67003f01r9372l6n96',
            center: centerCoordinates,
            zoom: 16,
            attributionControl: false,
        });

        // Add Geolocate Control
        const geolocateControl = new window.mapboxgl.GeolocateControl({
            positionOptions: { enableHighAccuracy: true },
            trackUserLocation: true,
            showUserLocation: true,
            showAccuracyCircle: true
        });

        mapRef.current.addControl(geolocateControl, 'bottom-right');

        mapRef.current.on('load', () => {
            console.log("Map loaded.");
            geolocateControl.trigger();
        });

        setTimeout(() => {
            const geoButton = document.querySelector('.mapboxgl-ctrl-bottom-right');
            if (geoButton) {
                geoButton.style.bottom = '100px';
                geoButton.style.right = '10px';
            }
        }, 500);
    }

    if (!mapRef.current && isIOS) {
        checkPermissionAndRequestLocation();
    }
  
    if (!mapRef.current) {
      navigator.permissions.query({ name: "geolocation" }).then((result) => {
        if (result.state === "granted" || result.state === "prompt" || result.state === "denied") {
          initMap(centerCoordinates);  
          const map = mapRef.current;
          let fieldCoordinates = [];
          if (fieldDetails?.data1?.fields) {
            fieldDetails.data1.fields.forEach((data) => {
              let coordinates =
                data.gpsPoints &&
                data.gpsPoints.map((gpsPoint) => [gpsPoint.longitude, gpsPoint.lattitude]);

              if (coordinates.length > 0) {
                fieldCoordinates.push({
                  type: "Feature",
                  geometry: { type: "Polygon", coordinates: [coordinates] },
                  properties: { name: data.kartDisplayName, id: data.fieldId },
                });
              }
            });
          }

          map.on('load', () => {
            const geojsonData = { type: 'FeatureCollection', features: fieldCoordinates };
            map.addSource('fields', { type: 'geojson', data: geojsonData });
            /* Loop through each field and add separate layers for each */
            fieldCoordinates.forEach((polygon) => {
              const fieldName = polygon.properties.name;
              const fieldId = polygon.properties.id;
              const coordinates = polygon.geometry.coordinates[0];
              const center = calculateCenter(coordinates);
              const markerElement = document.createElement('div');
              markerElement.className = `map-marker marker-${fieldId}`;

              const renderMarker = (selectedPolygonIdsRef, fieldId) => {
                ReactDOM.render(
                  <Marker
                    fieldId={fieldId}
                    selectedPolygonIdsRef={selectedPolygonIdsRef}
                  />,
                  markerElement
                );
              };

              /* Initial render */
              renderMarker(selectedPolygonIdsRef.current, fieldId);

              /* Add the marker to the map at the center of the polygon */
              new window.mapboxgl.Marker(markerElement)
                .setLngLat(center)
                .addTo(map);

              /* Add layer for each polygon */
              map.addLayer({
                id: `field-fill-${fieldId}`,
                type: 'fill',
                source: 'fields',
                paint: {
                  'fill-color': selectedPolygonIds && selectedPolygonIds.includes(fieldId) ? '#6df6fd' : '#00686b',
                  'fill-opacity': selectedPolygonIds && selectedPolygonIds.includes(fieldId) ? 0.8 : 0.08
                },
                //paint: { 'fill-color': selectedPolygonId === fieldId ? '#6df6fd' : '#00686b', 'fill-opacity': selectedPolygonId === fieldId ? 0.8 : 0.08 },
                filter: ['==', 'name', fieldName],
              });

              map.addLayer({
                id: `field-outline-${fieldId}`,
                type: 'line',
                source: 'fields',
                paint: { 'line-color': '#00696d', 'line-width': 1 },
                filter: ['==', 'name', fieldName],
              });

              /* Add marker source */
              const markerGeoJSON = {
                type: 'FeatureCollection',
                features: [
                  {
                    type: 'Feature',
                    geometry: {
                      type: 'Point',
                      coordinates: center,
                    },
                    properties: {
                      title: fieldName,
                    },
                  },
                ],
              };

              map.addSource(`field-marker-${fieldId}`, {
                type: 'geojson',
                data: markerGeoJSON,
              });          

              map.addLayer({
                id: `field-marker-layer-${fieldId}`,
                type: 'symbol',
                source: `field-marker-${fieldId}`,
                layout: {
                  'text-field': [
                    'format',
                    '   ',
                    { 'text-color': '#000000' },
                    ['get', 'title'],
                    { 'text-color': '#000000' },
                    '   '
                  ],
                  'text-font': ['Roboto Regular', 'Arial Unicode MS Regular'],
                  'text-size': 18,
                  'text-offset': [0, -2.75],
                  'text-anchor': 'top',
                  'text-padding': 4
                },
                paint: {
                  'text-halo-width': 2,          // Halo width: 2px
                  'text-halo-color': '#F7FAF9',  // Halo color: #F7FAF9
                  'text-halo-blur': 0            // Halo blur: 0px
                }
              });

              /* Mouse hover effect */
              map.on('mouseenter', `field-fill-${fieldId}`, (e) => {
                if (!selectedPolygonIdsRef.current.includes(fieldId)) {
                  map.setPaintProperty(`field-fill-${fieldId}`, 'fill-color', '#00686b'); // Highlight color
                  map.setPaintProperty(`field-fill-${fieldId}`, 'fill-opacity', 0.3);
                  map.getCanvas().style.cursor = 'pointer';
                }
              });

              map.on('mouseleave', `field-fill-${fieldId}`, () => {
                /* Ensure hover effect resets only if it's not the selected polygon */
                if (!selectedPolygonIdsRef.current.includes(fieldId)) {
                  map.setPaintProperty(`field-fill-${fieldId}`, 'fill-color', '#00686b');
                  map.setPaintProperty(`field-fill-${fieldId}`, 'fill-opacity', 0.08);
                }
                map.getCanvas().style.cursor = '';
              });

              /* Click effect */
              map.on('click', `field-fill-${fieldId}`, () => {
                mixpanel.identify(userInfo.data.id);
                mixpanel.track(KART_SELECT_FIELD);
              
                setShowModal(true);
                setHeight(halfHeight);
                setSelectedPolygonId(fieldId);
              
                setSelectedPolygonIds((prevSelected = []) => {
                  const newSelected = prevSelected.includes(fieldId)
                    ? prevSelected.filter(id => id !== fieldId) // Deselect
                    : [...prevSelected, fieldId]; // Select
              
                  selectedPolygonIdsRef.current = newSelected; // Update ref immediately
                  if(newSelected.length === 0) {
                    closeModal();
                  }
              
                  // Apply immediate color change to Mapbox
                  fieldCoordinates.forEach(polygon => {
                    const id = polygon.properties.id;
                    if (newSelected.includes(id)) {
                      map.setPaintProperty(`field-fill-${id}`, 'fill-color', '#6df6fd'); // Selected color
                      map.setPaintProperty(`field-fill-${id}`, 'fill-opacity', 0.8);
                    } else {
                      map.setPaintProperty(`field-fill-${id}`, 'fill-color', '#00686b'); // Default color
                      map.setPaintProperty(`field-fill-${id}`, 'fill-opacity', 0.08);
                    }
                  });
              
                  return newSelected;
                });

                setClickedArea((prevSelected = []) => {
                  const newSelected = prevSelected.includes(fieldName)
                    ? prevSelected.filter(name => name !== fieldName)
                    : [...prevSelected, fieldName];
                  return newSelected;
                });

                setClickedPolygonName((prevSelected = []) => {
                  const newSelected = prevSelected.includes(fieldName)
                    ? prevSelected.filter(name => name !== fieldName)
                    : [...prevSelected, fieldName];
                  return newSelected;
                });
              
                // Update marker icons immediately
                const markerElement = document.querySelector(`.marker-${fieldId}`);
                if (markerElement) {
                  ReactDOM.render(
                    selectedPolygonIdsRef.current.includes(fieldId) ? <CustomLocationOnIcon /> : <CustomLocationOffIcon />,
                    markerElement
                  );
                }
              });
            });
          });
        }
      });
    }
    // eslint-disable-next-line
  }, [props, props.fieldDetails, selectedPolygonId, selectedPolygonIds, props.geoLocDetails]);
  
  const Marker = ({ fieldId, selectedPolygonIdsRef }) => (
    <>
      {selectedPolygonIdsRef === fieldId ? (
        <CustomLocationOnIcon /> // Selected icon color
      ) : (
        <CustomLocationOffIcon />
      )}
    </>
  );

  /* Function to calculate the center of the polygon by averaging the coordinates */
  const calculateCenter = (coordinates) => {
    let area = 0;
    let centroidX = 0;
    let centroidY = 0;

    // Coordinates are assumed to be an array of [longitude, latitude]
    const n = coordinates.length;

    for (let i = 0; i < n; i++) {
      const [x1, y1] = coordinates[i];
      const [x2, y2] = coordinates[(i + 1) % n]; // Next coordinate, looping back to the first

      // Area calculation (Shoelace formula)
      const a = x1 * y2 - x2 * y1;
      area += a;

      // Centroid calculation (weighted average of coordinates)
      centroidX += (x1 + x2) * a;
      centroidY += (y1 + y2) * a;
    }

    // Final area and centroid coordinates
    area *= 0.5;
    centroidX /= (6 * area);
    centroidY /= (6 * area);

    // Return the centroid as [longitude, latitude]
    return [centroidX, centroidY];
  };

  const forceUpdate = React.useReducer(() => ({}), {})[1];

  /* Close the modal with animation */
  const closeModal = () => {
    console.log('closeModal');
    const { fieldDetails } = props;
    const map = mapRef.current;
    let fieldCoordinates = [];
    selectedPolygonIdsRef.current = [];
    if (fieldDetails?.data1?.fields) {
      fieldDetails.data1.fields.forEach((data) => {
        let coordinates =
          data.gpsPoints &&
          data.gpsPoints.map((gpsPoint) => [gpsPoint.longitude, gpsPoint.lattitude]);

        if (coordinates.length > 0) {
          fieldCoordinates.push({
            type: "Feature",
            geometry: { type: "Polygon", coordinates: [coordinates] },
            properties: { name: data.kartDisplayName, id: data.fieldId },
          });
        }
      });
    }
    fieldCoordinates && fieldCoordinates.forEach((polygon) => {
      const fieldId = polygon.properties.id;
      map.setPaintProperty(`field-fill-${fieldId}`, 'fill-color', '#00686b'); // Default color
      map.setPaintProperty(`field-fill-${fieldId}`, 'fill-opacity', 0.08);
      const markerElement = document.querySelector(`.marker-${fieldId}`);
      if (markerElement) {
        ReactDOM.render(
          <CustomLocationOffIcon />, // Default marker icon
          markerElement
        );
      }
    });
    setTimeout(() => {
      setShowModal(false);
      setSelectedPolygonId(null);
      setSelectedPolygonIds([]);
      setClickedPolygonName([]);
      setClickedArea([]);
      forceUpdate();
    }, 400);
    setIsRunning(false);
    setElapsedTime(0);
    setStartTime(null);
    setTotalHours(0);
    setRemainingMinutes(0);
  };

  useEffect(() => {
    if (openShowModal) {
      closeModal();
      closeSaveNewTask();
    }
    // eslint-disable-next-line
  }, [openShowModal]); 

  const getRecentActivities = () => {
    const { userActivitiesList, openNewTask, userInfo } = props;
    if (userActivitiesList.recent.activities?.length > 0) {
      return (
        userActivitiesList.recent.activities.map((userActivities) => {
          return (
            <Box sx={{ "&:hover": { backgroundColor: "#E6E9E8" }}} style={mapboxStyle.lastUsedBox} 
              onClick={() => {
                mixpanel.identify(userInfo.data.id);
                mixpanel.track(KART_SELECT_LAST_USED_TASK);
                setIsRunning(false);
                const { hours, minutes } = getElapsedTimeParts();
                openNewTask(userActivities, selectedPolygonIds, clickedPolygonName, hours, minutes);
              }}
            >
              <Box sx={{ paddingTop: '8px' }}>
                <AddIcon width="24" height="24"/>
              </Box>
              <Box>
                <Typography style={mapboxStyle.lastUsedBoxTitle} key={userActivities.id}>
                  {userActivities.activityName}
                </Typography>
              </Box>
            </Box>
          );
        })
      );
    }
    return null;
  };

  const getCategoryActivities = (category) => {
    const { userActivitiesList, openNewTask, userInfo } = props;
    let categoryActivities = null;
    if(category === 'harvest') { categoryActivities = userActivitiesList.harvest; }
    if(category === 'soil') { categoryActivities = userActivitiesList.soil; }
    if(category === 'fertilization') { categoryActivities = userActivitiesList.fertilization; }
    if(category === 'others') { categoryActivities = userActivitiesList.others; }
    if (categoryActivities.activities?.length > 0) {
      return (
        <Box>
          <Box sx={{ padding: '24px 5px 5px 5px', display: 'flex', justifyContent: 'flex-start' }}>
            <Typography style={mapboxStyle.modalTitleSubHeader}>{categoryActivities.name}</Typography>
          </Box>
          <Box>
            {categoryActivities.activities.map((userActivities) => {
              return userActivities.active && (
                <Box sx={{ "&:hover": { backgroundColor: "#E6E9E8" }}} style={mapboxStyle.lastUsedBoxLeft}
                  onClick={() => {
                    mixpanel.identify(userInfo.data.id);
                    mixpanel.track(KART_SELECT_TASK);
                    setIsRunning(false);
                    const { hours, minutes } = getElapsedTimeParts();
                    openNewTask(userActivities, selectedPolygonIds, clickedPolygonName, hours, minutes);
                  }}
                >
                  <Box sx={{ paddingTop: '8px' }}>
                    <AddIcon width="24" height="24"/>
                  </Box>
                  <Typography style={mapboxStyle.lastUsedBoxTitle} key={userActivities.id}>{userActivities.activityName}</Typography>
                </Box> )
              })
            }
          </Box>
        </Box>
      );
    }
    return null;
  };

  const handleTouchStart = (e) => {
   // e.preventDefault(); // Completely block default scrolling & gestures
    e.stopPropagation(); // Stop bubbling to the map
  
    setStartY(e.touches[0].clientY);
    setCurrentHeight(height);
  
    // Disable all map gestures
    if (mapRef.current) {
      mapRef.current.dragPan.disable();
      mapRef.current.scrollZoom.disable();
      mapRef.current.touchZoomRotate.disable();
    }
  };
  
  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
  
    const deltaY = startY - e.touches[0].clientY;
    const newHeight = Math.max(currentHeight + deltaY, initialHeight);
    setHeight(newHeight);
  };
  
  const handleTouchEnd = () => {
    if (height < (halfHeight + initialHeight) / 2) {
      setHeight(initialHeight);
    } else if (height < (maxHeight + halfHeight) / 2) {
      setHeight(halfHeight);
    } else {
      setHeight(maxHeight);
    }
  
    // Re-enable Mapbox interactions
    if (mapRef.current) {
      mapRef.current.dragPan.enable();
      mapRef.current.scrollZoom.enable();
      mapRef.current.touchZoomRotate.enable();
    }
  };  

  /* Modal rendering logic */
  const { intl, history, userInfo } = props;
  return (
    <Box>
      <div
        ref={mapContainerRef}
        className="map-container-grey"
      />
      {/* Modal that opens from the bottom */}
      {showModal && (
        <>
          {/* Backdrop */}
          <Box
            sx={{
              position: 'fixed', bottom: 0, left: 0, width: '100%', height: `${height}px`, zIndex: 15, touchAction: 'none',
              borderRadius: '20px 20px 0px 0px', background: '#F2F4F4', transition: 'height 0.3s ease-in-out',
              overflow: 'hidden', display: 'flex', flexDirection: 'column',
              boxShadow: '0px -4px 10px rgba(0, 0, 0, 0.15)'
            }}
          >
            {/* Drag Handle */}
            <Box
              onTouchStart={handleTouchStart}
              onTouchMove={handleDrag}
              onTouchEnd={handleTouchEnd}
            >
              <Box sx={{ textAlign: 'center', padding: '15px 8px 15px 8px' }}>
                <Box sx={{ width: 40, height: 5, backgroundColor: '#7A757F', borderRadius: 2, margin: '0 auto' }} />
              </Box>

              {/* Header */}

              <Box style={{ ...mapboxStyle.modalTitle, zIndex: 20, position: 'relative', display: 'flex', alignItems: 'center' }}>
                {/* Close Button */}
                <Box
                  sx={{ width: 48, height: 48, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}
                  onClick={closeModal}
                >
                  <CloseIcon />
                </Box>

                {/* Title */}
                <Typography style={mapboxStyle.modalTitleHeader} sx={{ paddingLeft: '12px' }}>
                  {clickedArea.length > 1 ? `${clickedArea.length} ${intl.formatMessage({ id: 'SKIFTER_VALGT' })}` : clickedArea[0]}
                </Typography>

                {/* Timer Section */}
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', marginLeft: 'auto', marginRight: 2 }}>
                  <IconButton
                    onClick={handleStartStop}
                    sx={{
                      backgroundColor: isRunning ? '#D4F1F3' : '#E9E9E9',
                      borderRadius: '50%',
                      width: 48,
                      height: 48,
                      '&:hover': { backgroundColor: isRunning ? '#B0E4E7' : '#E9E9E9' }
                    }}
                  >
                    {isRunning ? <StopIcon sx={{ color: '#002b2d' }} /> : <PlayArrowIcon sx={{ color: '#002b2d' }} />}
                  </IconButton>
                  <Typography variant="body2" sx={{ marginTop: '6px', fontWeight: 500 }}>
                    {formatElapsedFull()}
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* Content */}
            <Box sx={{ paddingRight: '15px', paddingLeft: '15px', flexGrow: 1, overflowY: 'auto' }}>
              <Box sx={{ paddingRight: '20px', display: 'flex', justifyContent: 'flex-end', borderTop: '1px solid #cccccc' }}>
                <Typography style={mapboxStyle.modalTitleSubHeader} sx={{paddingTop: '12px'}}>
                  {intl.formatMessage({ id: 'LAST_USED' })}
                </Typography>
              </Box>
              <Box sx={{ padding: '0px 20px 100px 20px' }}>
                {getRecentActivities()}
                {['harvest', 'soil', 'fertilization', 'others'].map((cat) => (
                  <Box key={cat}>{getCategoryActivities(cat)}</Box>
                ))}
              </Box>
            </Box>

            {/* Bottom Actions - Only Show When Height is >= 50% */}
            {height >= halfHeight && (
              <Box
                sx={{
                  position: 'absolute', bottom: 0, width: '100%', background: '#F1F4F4', zIndex: 25,
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '25px 30px 40px 30px'
                }}
                onClick={() => {mixpanel.identify(userInfo.data.id); mixpanel.track(CLICK_STD_SETTINGS); history.push('/settings/kart'); closeModal();}}
              >
                <Typography style={mapboxStyle.lastUsedBoxTitle}>{intl.formatMessage({ id: 'TOGGLE_SETTINGS' })}</Typography>
                <ArrowIcon />
              </Box>
            )}
          </Box>
        </>
      )}
    </Box>
  );
});
export default MapBox;